/*eslint max-lines-per-function: ["error", 285]*/
import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { uniqBy } from 'lodash';

import {
  CreateUpdateOrderDto,
  GetEmployeeDto,
  GetEstimateItemDto,
  GetOrderEstimateItemDto,
  GetProjectDto,
  GetTeamDto,
} from '../../../common/pokCore/autogenerated/pokApiClient/models';
import FormRow from '../../../app/components/FormRow';
import { Option } from '../Selects/Selector';
import TeamSelector from '../Selects/TeamSelector';
import EmployeeSelector from '../Selects/EmployeeSelector';
import DynamicTextarea from '../../../app/components/DynamicTextarea';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { getOptionValues } from '../Selects/selectUtils';
import { NewDateRangePicker } from '../../../app/components/NewDateRangePicker';
import DictionarySelector from '../Selects/DictionarySelector';
import { TooltipKey } from '../../../common/pokCore/tooltip.enum';

import { OrderEstimateItemsGrid } from './OrderEstimateItemsGrid';

interface OrderFormProps {
  order: CreateUpdateOrderDto;
  propertyChange?: (obj: Partial<CreateUpdateOrderDto>) => void;
  emails: string[];
  setEmails?: (emails: string[]) => void;
  setOrderEstimateItems?: (emails: string[]) => void;
  project?: GetProjectDto;
  resultProject?: GetProjectDto;
  readOnly?: boolean;
  orderEstimateItems?: GetOrderEstimateItemDto[];
  currencyCode?: string | undefined;
  onEstimateItemsFilterChanged?: (changed: boolean) => void;
}

export const OrderForm: React.FC<OrderFormProps> = ({
  order,
  propertyChange,
  setOrderEstimateItems,
  emails,
  setEmails,
  readOnly,
  project,
  resultProject,
  orderEstimateItems,
  currencyCode,
  onEstimateItemsFilterChanged,
}) => {
  const { t, tk } = useTranslation('orderLng');
  const pok = usePokCore();
  const notifications = useNotifications();

  const [allEstimateItems, setAllEstimateItems] = useState<
    GetEstimateItemDto[]
  >([]);
  const [estimateItems, setEstimateItems] = useState<GetEstimateItemDto[]>([]);
  const [fetchedEmployees, setFetchedEmployees] = useState<GetEmployeeDto[]>(
    [],
  );
  const [fetchedTeams, setFetchedTeams] = useState<GetTeamDto[]>([]);
  const [filterChanged, setFilterChanged] = useState(false);

  const generateEmailsList = (): string[] => {
    const teamEmail = fetchedTeams.find(
      team => team.id === order.teamId,
    )?.email;

    const employeesEmails = order.orderEmployeesIds.map(employee => {
      return fetchedEmployees.find(fE => fE.id === employee)?.email;
    });

    const emailsList = order.addTeamToEmails
      ? [teamEmail, ...employeesEmails]
      : employeesEmails;

    return emailsList.filter(value => !!value) as string[];
  };

  const getUniquePhases = (items: GetEstimateItemDto[]) =>
    uniqBy(
      items.filter(item => item.pathPhaseDictionary?.id),
      item => item.pathPhaseDictionary!.id,
    ).map(item => item.pathPhaseDictionary!);

  const filterItems = (
    items: GetEstimateItemDto[],
    phaseId?: string,
    startDate?: Date,
    endDate?: Date,
  ) => {
    return items.filter(item => {
      if (phaseId && item.pathPhaseDictionary?.id !== phaseId) {
        return false;
      }
      if (startDate && item.endDate && item.endDate < startDate) {
        return false;
      }
      if (endDate && item.startDate && item.startDate > endDate) {
        return false;
      }
      return true;
    });
  };

  const getEstimateItemsForOrder = (
    items: GetEstimateItemDto[],
    phaseId?: string,
  ) => {
    if (!filterChanged && order.id && orderEstimateItems) {
      const originalItemsIds = orderEstimateItems
        .filter(({ active }) => active)
        .map(({ estimateItem }) => estimateItem.id);
      return items.filter(({ id }) => originalItemsIds.includes(id));
    }
    return filterItems(items, phaseId, order.startDate, order.endDate);
  };

  const markFilterChanged = () => {
    setFilterChanged(true);
    onEstimateItemsFilterChanged?.(true);
  };

  const handleSetFetchedEmployees = (options: GetEmployeeDto[]) =>
    setFetchedEmployees(options);

  const handleSetFetchedTeams = (options: GetTeamDto[]) =>
    setFetchedTeams(options);

  const handleTeamChange = (option: unknown) => {
    const newTeamId = (option as Option)?.value || undefined;
    if (newTeamId !== order.teamId) {
      setAllEstimateItems([]);
      markFilterChanged();
      propertyChange?.({ teamId: newTeamId, pathPhaseDictionaryId: undefined });
    } else {
      propertyChange?.({ teamId: newTeamId });
    }
  };

  useEffect(() => {
    if (order.teamId) {
      pok.estimateItems
        .findAllByProjectAndTeam(order.projectId, order.teamId)
        .then(data => {
          setAllEstimateItems(data);
          const phases = getUniquePhases(data);
          const phaseId =
            order.pathPhaseDictionaryId ??
            (phases.length === 1 ? phases[0].id : undefined);
          if (phaseId && !order.pathPhaseDictionaryId) {
            propertyChange?.({ pathPhaseDictionaryId: phaseId });
          }
          const filtered = getEstimateItemsForOrder(data, phaseId);
          setEstimateItems(filtered);
          setOrderEstimateItems?.(filtered.map(({ id }) => id));
        })
        .catch(errorResponse => {
          notifications.caughtError(errorResponse);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications, order.projectId, order.teamId, pok.estimateItems]);

  useEffect(() => {
    if (!filterChanged && order.id && orderEstimateItems) {
      return;
    }
    const filtered = getEstimateItemsForOrder(
      allEstimateItems,
      order.pathPhaseDictionaryId,
    );
    setEstimateItems(filtered);
    setOrderEstimateItems?.(filtered.map(({ id }) => id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    order.pathPhaseDictionaryId,
    order.startDate,
    order.endDate,
    filterChanged,
  ]);

  useEffect(() => {
    setEmails?.(generateEmailsList());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchedTeams,
    order.teamId,
    fetchedEmployees,
    order.orderEmployeesIds,
    order.addTeamToEmails,
  ]);

  const uniquePhases = getUniquePhases(allEstimateItems);

  return (
    <Form className="d-grid gap-4 mb-3">
      {project && (
        <FormRow controlId="project" label={t(tk.prLang.project)}>
          <Form.Control
            value={`${project.name} (${project?.number})`}
            disabled
          />
        </FormRow>
      )}
      {resultProject && (
        <FormRow
          controlId="resultProject"
          label={t(tk.prLang.orderResultProject)}
        >
          <Form.Control
            value={`${resultProject.name} (${resultProject?.number})`}
            disabled
          />
        </FormRow>
      )}
      <FormRow controlId="team" label={t(tk.order.specialTeam)}>
        <TeamSelector
          value={order.teamId}
          projectId={order.projectId}
          setOptions={handleSetFetchedTeams}
          onChange={handleTeamChange}
          readOnly={readOnly}
        />
      </FormRow>
      {project?.company?.pathPhase &&
        (order.pathPhaseDictionaryId || uniquePhases.length > 0) && (
          <FormRow
            controlId="pathPhaseDictionary"
            label={t(tk.common.positionPhase)}
            tooltipKey={TooltipKey.orderPhaseTooltip}
          >
            <DictionarySelector
              dictionary="Faza"
              value={order.pathPhaseDictionaryId}
              allowableValues={
                uniquePhases.length > 0 ? uniquePhases : undefined
              }
              clearable={false}
              readOnly={readOnly}
              onChange={option => {
                markFilterChanged();
                propertyChange?.({
                  pathPhaseDictionaryId: option?.value || undefined,
                });
              }}
            />
          </FormRow>
        )}
      <FormRow controlId="dateRange" label={t(tk.common.rangeMonth)}>
        <NewDateRangePicker
          disabled={readOnly}
          value={[order.startDate ?? null, order.endDate ?? null]}
          onChange={value => {
            markFilterChanged();
            if (value) {
              const [start, end] = value as [Date | null, Date | null];
              propertyChange?.({
                startDate: start ?? undefined,
                endDate: end ?? undefined,
              });
            } else {
              propertyChange?.({ startDate: undefined, endDate: undefined });
            }
          }}
        />
      </FormRow>
      <FormRow controlId="employees" label={t(tk.order.receivers)}>
        <EmployeeSelector
          value={order.orderEmployeesIds}
          isMulti
          fetchForOrders
          setOptions={handleSetFetchedEmployees}
          onChange={options =>
            propertyChange?.({
              orderEmployeesIds: getOptionValues(options),
            })
          }
          readOnly={readOnly}
        />
      </FormRow>
      <FormRow
        controlId="sendEmail"
        label={t(tk.order.sendEmailOfToSpecialTeam)}
      >
        <Form.Check
          type="checkbox"
          className="pt-2"
          checked={order.addTeamToEmails}
          onChange={e => {
            const { checked } = e.target;
            propertyChange?.({
              addTeamToEmails: checked,
            });
          }}
          disabled={readOnly}
        />
      </FormRow>
      <FormRow controlId="emails" label={t(tk.common.emails)}>
        <DynamicTextarea value={emails.join(', ')} disabled />
      </FormRow>
      <FormRow
        controlId="estimateItems"
        label={t(tk.prLang.teamRelatedEstimates)}
      >
        <OrderEstimateItemsGrid
          data={estimateItems}
          currencyCode={currencyCode}
        />
      </FormRow>
    </Form>
  );
};
